import { Info } from '@clearkit/icons';
import { CKBannerNext, CKBannerVariant, CKButton, CKIconStack } from 'clearkit';

import Logo from '~/components/ConfigurationLogo';
import { AccountNotificationFragment } from '~/generated/graphql';
import { Configuration, purposeForConnectorService } from '~/lib/configuration';
import { readableType } from '~/lib/dataEnrichment';
import history from '~/lib/history';
import paths from '~/lib/paths';

import { buildAccountNotification } from './accountNotificationsUtils';

export type AccountNotificationProps = {
  accountNotification: AccountNotificationFragment;
  className?: string;
  fixIssue?: () => void;
  icon?: React.ReactNode;
  isFromEnrichmentPage?: boolean;
  isFullWidth?: boolean;
  showResolveButton?: boolean;
  variant?: CKBannerVariant;
};

export const AccountNotification: React.FC<AccountNotificationProps> = ({
  accountNotification,
  className,
  icon,
  isFullWidth,
  showResolveButton = true,
  fixIssue = () => history.push(paths.configurations()),
  isFromEnrichmentPage,
  variant = 'warning',
}) => {
  const {
    sourceName,
    notificationType,
    message,
    segmentType,
    segmentId,
    sourceId,
  } = accountNotification;

  let resolve = fixIssue;

  const {
    documentationLink,
    helpText,
    isAccountConnectorNotification,
    isDestinationNotification,
    isEnrichmentNotification,
    messageDescription,
    messageTitle,
    shouldRenderNotification,
  } = buildAccountNotification(
    message,
    notificationType,
    readableType(sourceName),
  );

  if (isDestinationNotification) {
    resolve = () =>
      history.push(
        paths.segmentDestinationEdit(segmentType, segmentId, sourceId),
      );
  } else if (documentationLink) {
    resolve = () => window.open(documentationLink);
  }

  if (!shouldRenderNotification(!!isFromEnrichmentPage)) {
    return null;
  }

  const action =
    notificationType === 'account_connector_auth' ? 'Reconnect' : 'Resolve';

  const hasNoMessage =
    ((!isAccountConnectorNotification && !isDestinationNotification) ||
      !sourceName) &&
    !isEnrichmentNotification;

  let messageText;

  if (isEnrichmentNotification) {
    messageText = `Please verify that the Clearbit integration user has permissions
      to read/write fields in ${readableType(sourceName)}.`;
  } else {
    messageText = `${action} to continue ${purposeForConnectorService(
      sourceName as Configuration,
    )}`;
  }

  const hasMessagePlusTitle =
    (!!messageDescription && !!messageTitle) ||
    (!!messageDescription && !!messageText);

  return (
    <CKBannerNext
      className={className}
      data-testid="account-notification-container"
      isFullWidth={isFullWidth}
      variant={variant}
    >
      {(isAccountConnectorNotification || isDestinationNotification) && icon ? (
        <CKBannerNext.Icon>
          <CKIconStack
            badge={<Info className="fill-gradient-br-yellow" />}
            base={<Logo type={sourceName as Configuration} />}
          />
        </CKBannerNext.Icon>
      ) : (
        <CKBannerNext.IconStatus>{icon || <Info />}</CKBannerNext.IconStatus>
      )}
      {messageTitle || hasMessagePlusTitle ? (
        <CKBannerNext.Header>
          {messageTitle || messageDescription}
        </CKBannerNext.Header>
      ) : null}
      <CKBannerNext.Body>
        {!hasNoMessage ? <span>{messageText}</span> : null}
        {!hasMessagePlusTitle ? (
          <span>
            {messageDescription ||
              `There was an error ${purposeForConnectorService(
                sourceName as Configuration,
              )}`}
          </span>
        ) : null}
      </CKBannerNext.Body>

      {showResolveButton && !isEnrichmentNotification ? (
        <CKBannerNext.CTA>
          <CKButton
            data-testid="fix-issue-button"
            onClick={resolve}
            variant="bold"
            variantColor="yellow"
          >
            {helpText}
          </CKButton>
        </CKBannerNext.CTA>
      ) : null}
    </CKBannerNext>
  );
};
